// drawCompile.ts — Patch 010.C.2.3 (Deep Draws, sub-patch 3 of 3).
//
// Compile + submit, payment confirmation, GC-fee tracking, closeout readiness.
// Backend: workers/core-os/src/routes/draw_compile.ts (migration 0067 —
// draw_requests compiled_json / compiled_at / is_final_draw).

import { request } from './request'

const pid = (id: string): string => encodeURIComponent(id)

export interface CompileWarning {
  invoice_id: string
  vendor: string
  field: 'pdf' | 'scs_line'
  detail: string
}

export interface ChangeOrderPending {
  change_order_id: string
  scs_line_id: string
  scs_line_number: number | null
  scs_line_display_name: string
  original_budget_cents: number
  signed_co_total_cents: number
  effective_budget_cents: number
  approved_invoice_total_cents: number
  delta_cents: number
  status: string
  newly_created: boolean
}

export interface CompileResult {
  ok: true
  draw_id: string
  gmail_draft_id: string
  gmail_draft_url: string
  total_requested_cents: number
  construction_cost_requested_cents: number
  invoice_count: number
  attachment_count: number
  attachments_skipped: number
  lender_field_count: number
  title_field_count: number
  // Patch 016.1.L — low-confidence auto-matches that didn't block compile.
  // Render alongside the success notice so the operator verifies the
  // flagged rows before clicking Send in Gmail. Empty when clean.
  warnings?: CompileWarning[]
  // Patch 016.1.Q — over-budget SCS lines auto-create change_orders rows.
  // Phase 1 surfaces the math; Phase 2.B attaches the filled CO PDF to
  // the Gmail draft when the CO template is uploaded + parsed.
  change_orders_pending?: ChangeOrderPending[]
  // Patch 016.1.Q Phase 2.B — filled CO PDF metadata. Present when the
  // CO template was uploaded + parsed AND the Render fill succeeded.
  co_pdf?: {
    document_id: string
    filename: string
    co_number: number
    size_bytes: number
  } | null
  // Reasons the CO PDF couldn't be generated. Not blockers.
  co_pdf_warnings?: string[]
  // 0126 — GC fee has no draw_invoices row (would double-count invoiceSum),
  // so a missing/failed-to-attach GC-fee invoice PDF can't surface via
  // `warnings` above. Not a blocker; render alongside it.
  gc_fee_warnings?: string[]
  request_id: string
}

export interface ConfirmPaymentResult {
  ok: true
  draw_id: string
  status: string
  invoices_marked_paid: number
  total_funded_cents: number
  request_id: string
}

export interface CloseoutReadiness {
  ok: true
  draw_id: string
  is_final_draw: boolean
  ready: boolean
  checks: {
    all_invoices_paid: { ok: boolean; unpaid_count: number }
    all_lien_waivers_received: { ok: boolean; unreceived_count: number }
    scs_lines_within_budget: {
      ok: boolean
      over_budget: Array<{
        scs_line_id: string
        display_name: string
        approved_cents: number
        current_budget_cents: number
      }>
    }
  }
}

export interface GcFeeDraw {
  id: string
  draw_number: number
  status: string
  gc_fee_amount_cents: number | null
  gc_fee_percent_basis_points: number | null
  funded_at: string | null
}

export interface GcFeeResponse {
  ok: true
  collected_cents: number
  planned_cents: number
  target_cents: number | null
  draws: GcFeeDraw[]
}

export const drawCompileApi = {
  compile: (projectId: string, drawId: string) =>
    request<CompileResult>(
      `/api/projects/${pid(projectId)}/draws/${pid(drawId)}/compile`,
      { method: 'POST', body: '{}' },
    ),
  confirmPayment: (
    projectId: string,
    drawId: string,
    body?: { funded_at?: string; total_funded_cents?: number },
  ) =>
    request<ConfirmPaymentResult>(
      `/api/projects/${pid(projectId)}/draws/${pid(drawId)}/confirm-payment`,
      { method: 'POST', body: JSON.stringify(body ?? {}) },
    ),
  closeoutReadiness: (projectId: string, drawId: string) =>
    request<CloseoutReadiness>(
      `/api/projects/${pid(projectId)}/draws/${pid(drawId)}/closeout-readiness`,
    ),
  gcFee: (projectId: string) =>
    request<GcFeeResponse>(`/api/projects/${pid(projectId)}/gc-fee`),
}
